import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { RiskScoringModel as Risk_Scoring_Model_Enum } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RiskRatingResolver } from 'src/ratings/useRiskRatingResolver';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import {
  useGetLatestInternalAuditReportRiskAssessmentResultsByRiskId,
  useGetLatestOversightAssessmentRiskAssessmentResultsByRiskId,
} from '@/hooks/queries';
import { useAggregation } from '@/hooks/useAggregation';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useRiskScore } from '@/hooks/useRiskScore';
import { useConfigIds } from '@/ratings/useConfigIds';
import { toLocalDate } from '@/utils/dateUtils';

import { RiskScoreBreakdownPopover } from '../../risk-score-breakdown/RiskScoreBreakdownPopover';
import { SECTION_IDS } from '../constants';
import type {
  RatingSectionData,
  UseRiskOverviewPanelRatingsResult,
} from '../types';

interface SectionValues {
  rating: number | null | undefined;
  score?: number | null;
  likelihood: number | null | undefined;
  impact: number | null | undefined;
  completionDate: string | null | undefined;
  configId?: string | null;
}

interface UseRiskOverviewPanelRatingsOptions {
  canViewOversight?: boolean;
  canViewInternalAudit?: boolean;
}

const formatDate = (date: string | null | undefined) =>
  date ? toLocalDate(date) : undefined;

export const useRiskOverviewPanelRatings = (
  riskId: string,
  {
    canViewOversight,
    canViewInternalAudit,
  }: UseRiskOverviewPanelRatingsOptions = {}
): UseRiskOverviewPanelRatingsResult => {
  const { t } = useTranslation('common', { keyPrefix: 'riskOverviewPanel' });
  const score = useRiskScore(riskId);
  const { riskModel } = useAggregation();

  const { data: oversightResults, loading: oversightLoading } =
    useGetLatestOversightAssessmentRiskAssessmentResultsByRiskId({
      queryArgs: { riskId },
      shouldSkip: !canViewOversight,
    });

  const { data: internalAuditResults, loading: internalAuditLoading } =
    useGetLatestInternalAuditReportRiskAssessmentResultsByRiskId({
      queryArgs: { riskId },
      shouldSkip: !canViewInternalAudit,
    });

  const oversightRecords = useMemo(
    () => [
      ...(oversightResults?.uncontrolled ?? []),
      ...(oversightResults?.controlled ?? []),
    ],
    [oversightResults]
  );
  const internalAuditRecords = useMemo(
    () => [
      ...(internalAuditResults?.uncontrolled ?? []),
      ...(internalAuditResults?.controlled ?? []),
    ],
    [internalAuditResults]
  );
  const oversightConfigIds = useConfigIds(oversightRecords);
  const internalAuditConfigIds = useConfigIds(internalAuditRecords);

  const {
    inherentRating,
    inherentScore,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    inherentConfigId,
    residualRating,
    residualScore,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    residualConfigId,
    breakdownData,
    scoreModifiedAt,
    showScore,
  } = score;

  const configIds = useMemo(
    () => [
      ...new Set(
        [inherentConfigId, residualConfigId].filter(
          (id): id is string => id != null
        )
      ),
    ],
    [inherentConfigId, residualConfigId]
  );

  // Scoring-settings (matrix) resolution is not yet enabled for the
  // oversight / internal_audit config types, so these resolvers
  // currently fall back to the taxonomy lookups inside useRiskRatingResolver
  // and the configIds/per-row ConfigId are effectively unused. They are passed
  // anyway to match the Assessments-tab registers (complianceRatingConfig /
  // internalAuditRatingConfig) and so matrix resolution works automatically
  // once scoring settings is wired up for these types.
  const standardResolver = useRiskRatingResolver('standard', {
    configType: 'risk_assessment',
    configIds,
  });
  const oversightResolver = useRiskRatingResolver('oversight', {
    configType: 'oversight',
    configIds: oversightConfigIds,
    skip: !canViewOversight,
  });
  const internalAuditResolver = useRiskRatingResolver('internal_audit', {
    configType: 'internal_audit',
    configIds: internalAuditConfigIds,
    skip: !canViewInternalAudit,
  });

  const isTypedModel =
    riskModel === Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages;
  const isBreakdownModuleEnabled = useIsModuleEnabled(
    'risk.subModules.risk_score_breakdown'
  );
  const showBreakdownPopover =
    isBreakdownModuleEnabled && isTypedModel && breakdownData !== null;

  const buildSection = useCallback(
    ({
      resolver,
      id,
      title,
      controlType,
      values,
      defaultExpanded,
      adornment,
      scoreNotCalculated,
    }: {
      resolver: RiskRatingResolver;
      id: string;
      title: string;
      controlType: Risk_Assessment_Result_Control_Type_Enum;
      values: SectionValues;
      defaultExpanded: boolean;
      adornment?: ReactNode;
      scoreNotCalculated?: boolean;
    }): RatingSectionData => ({
      id,
      title,
      overallRating: resolver.resolveRiskRating({
        likelihood: values.likelihood ?? null,
        impact: values.impact ?? null,
        controlType,
        rating: values.rating,
        configId: values.configId,
      }),
      score: values.score,
      formattedDate: formatDate(values.completionDate),
      rows: [
        {
          label: t('ratings.likelihoodLabel'),
          rating: resolver.resolveLikelihood(
            values.likelihood,
            values.configId
          ),
          formattedDate: formatDate(values.completionDate),
        },
        {
          label: t('ratings.impactLabel'),
          rating: resolver.resolveImpact(values.impact, values.configId),
          formattedDate: formatDate(values.completionDate),
        },
      ],
      defaultExpanded,
      adornment,
      ...(scoreNotCalculated ? { scoreNotCalculated } : {}),
    }),
    [t]
  );

  // First-line inherent/residual sections come from useRiskScore: the L/I
  // resolve from a fast sync query but the score arrives on the subscription
  // ~2s later, so rendering live data tears (label/colour flip while the score
  // is stale). These sections are latched to each score push below.
  const liveFirstLineSections = useMemo((): RatingSectionData[] => {
    // Only aggregation models score into `risk_score`; under the default model
    // the first-line values come from the assessments and no row is expected.
    const scoreNotCalculated = showScore === true && scoreModifiedAt === null;
    const residualAdornment =
      showBreakdownPopover && breakdownData ? (
        <RiskScoreBreakdownPopover
          breakdown={breakdownData}
          triggerLabel={t('ratings.breakdownPopoverTriggerLabel', {
            defaultValue: 'Why this score?',
          })}
        />
      ) : undefined;

    return [
      buildSection({
        resolver: standardResolver,
        id: SECTION_IDS.inherent,
        title: t('ratings.inherentTitle'),
        controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
        values: {
          rating: inherentRating,
          score: inherentScore,
          likelihood: inherentLikelihood,
          impact: inherentImpact,
          completionDate: inherentCompletionDate,
          configId: inherentConfigId,
        },
        defaultExpanded: true,
        scoreNotCalculated,
      }),
      buildSection({
        resolver: standardResolver,
        id: SECTION_IDS.residual,
        title: t('ratings.residualTitle'),
        controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
        values: {
          rating: residualRating,
          score: residualScore,
          likelihood: residualLikelihood,
          impact: residualImpact,
          completionDate: residualCompletionDate,
          configId: residualConfigId,
        },
        defaultExpanded: true,
        adornment: residualAdornment,
        scoreNotCalculated,
      }),
    ];
  }, [
    t,
    buildSection,
    standardResolver,
    showScore,
    scoreModifiedAt,
    inherentRating,
    inherentScore,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    inherentConfigId,
    residualRating,
    residualScore,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    residualConfigId,
    showBreakdownPopover,
    breakdownData,
  ]);

  // Oversight and internal audit sections come from their own GraphQL
  // queries — rating/L/I/date all arrive together, so there
  // is no score-vs-L/I tear and they render live (not latched). Latching them
  // would freeze the sections empty until the next score push.
  const oversightSections = useMemo((): RatingSectionData[] => {
    const sectionList: RatingSectionData[] = [];

    if (canViewOversight) {
      const uncontrolled = oversightResults?.uncontrolled[0];
      const controlled = oversightResults?.controlled[0];
      sectionList.push(
        buildSection({
          resolver: oversightResolver,
          id: SECTION_IDS.inherentOversight,
          title: t('ratings.inherentOversightTitle'),
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          values: {
            rating: uncontrolled?.Rating,
            score: null,
            likelihood: uncontrolled?.Likelihood,
            impact: uncontrolled?.Impact,
            completionDate: uncontrolled?.TestDate,
            configId: uncontrolled?.ConfigId,
          },
          defaultExpanded: false,
        }),
        buildSection({
          resolver: oversightResolver,
          id: SECTION_IDS.residualOversight,
          title: t('ratings.residualOversightTitle'),
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          values: {
            rating: controlled?.Rating,
            score: null,
            likelihood: controlled?.Likelihood,
            impact: controlled?.Impact,
            completionDate: controlled?.TestDate,
            configId: controlled?.ConfigId,
          },
          defaultExpanded: false,
        })
      );
    }

    if (canViewInternalAudit) {
      const uncontrolled = internalAuditResults?.uncontrolled[0];
      const controlled = internalAuditResults?.controlled[0];
      sectionList.push(
        buildSection({
          resolver: internalAuditResolver,
          id: SECTION_IDS.inherentInternalAudit,
          title: t('ratings.inherentInternalAuditTitle'),
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          values: {
            rating: uncontrolled?.Rating,
            score: null,
            likelihood: uncontrolled?.Likelihood,
            impact: uncontrolled?.Impact,
            completionDate: uncontrolled?.TestDate,
            configId: uncontrolled?.ConfigId,
          },
          defaultExpanded: false,
        }),
        buildSection({
          resolver: internalAuditResolver,
          id: SECTION_IDS.residualInternalAudit,
          title: t('ratings.residualInternalAuditTitle'),
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          values: {
            rating: controlled?.Rating,
            score: null,
            likelihood: controlled?.Likelihood,
            impact: controlled?.Impact,
            completionDate: controlled?.TestDate,
            configId: controlled?.ConfigId,
          },
          defaultExpanded: false,
        })
      );
    }

    return sectionList;
  }, [
    t,
    buildSection,
    canViewOversight,
    canViewInternalAudit,
    oversightResolver,
    internalAuditResolver,
    oversightResults,
    internalAuditResults,
  ]);

  // Latch the first-line sections to each score push: hold the last coherent
  // snapshot, flip atomically when `scoreModifiedAt` advances. Guard on
  // `!loading` so we never latch before the sync L/I have loaded; reset when
  // the risk changes.
  //
  // `scoreModifiedAt` comes from the `risk_score` row, but `score.loading`
  // tracks the (separate) inherent/residual assessment-result query. A risk
  // can therefore finish loading with resolvable L/I while `scoreModifiedAt`
  // stays null — notably the default scoring model, whose L/I come from the
  // assessment results, not a `risk_score` row. Keying re-latch solely on
  // `scoreModifiedAt` advancing would leave those risks stuck on the empty
  // mount snapshot, so `settled` seeds the latch once loading finishes.
  const [latched, setLatched] = useState<{
    riskId: string;
    settled: boolean;
    scoreModifiedAt: string | null;
    sections: RatingSectionData[];
  }>({
    riskId,
    settled: false,
    scoreModifiedAt: null,
    sections: liveFirstLineSections,
  });

  if (latched.riskId !== riskId) {
    // Panel switched to a different risk — drop the previous risk's snapshot.
    setLatched({
      riskId,
      settled: false,
      scoreModifiedAt: null,
      sections: liveFirstLineSections,
    });
  } else if (
    !score.loading &&
    (!latched.settled || scoreModifiedAt !== latched.scoreModifiedAt)
  ) {
    // Any change re-latches, in either direction. A recalculation deletes
    // every score row before writing the first, so `scoreModifiedAt` going
    // timestamp → null is a real transition — and the one RSP-7301 exists to
    // show. Requiring the new value to be non-null held the stale
    // pre-recalculation score on screen instead of "Not calculated". `settled`
    // covers the initial null, so this cannot latch in a loop.
    setLatched({
      riskId,
      settled: true,
      scoreModifiedAt,
      sections: liveFirstLineSections,
    });
  }

  return {
    data: [...latched.sections, ...oversightSections],
    loading:
      score.loading ||
      (!!canViewOversight && oversightLoading) ||
      (!!canViewInternalAudit && internalAuditLoading),
  };
};
